import React, { useEffect, useState } from 'react';
import { Formik, Form, FormikHelpers, FieldArray, ErrorMessage } from 'formik';
import Button from 'components/Button/Index';
import Activation from 'components/feature/activation/Index';
import ActionButton from 'components/ActionButton/Index';
import FormField from 'components/FormField/Index';
import useLocales from 'hooks/useLocales';
import { InputType } from 'core/enum/inputType';
import { validationSchema } from './form';
import { CMSService } from 'services/cms/cms';
import Popup from 'components/shared/Popups/Popup/Index';
import ConfirmPopup from 'components/shared/Popups/Confirm/Index';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import './Index.scss';

const benefitHasChanged = (currentBenefit: any, originalBenefit: any) => {
  let hasChanged = false;
  const changedFields: any = {};
  if (currentBenefit.description !== originalBenefit.description) {
    hasChanged = true;
  }
  if (currentBenefit.title !== originalBenefit.title) {
    hasChanged = true;
  }
  if (hasChanged) {
    changedFields.description = currentBenefit.description;
    changedFields.title = currentBenefit.title;
    changedFields.page = currentBenefit.page;

    return {
      id: currentBenefit.id,
      page: currentBenefit.page,
      changedFields
    };
  }
  return null;
};

const OurBenefits = ({
  onSubmit,
  onBack,
  onClose,
  setCompanyData,
  companyData,
  isPopup = false,
  className,
  isOneBtn = false,
  benefitData,
  setBenFits,
  showTopSide,
  setNewData,
  setBenefitData,
  showTitle
}: any) => {
  const { t } = useLocales();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDeleteItem, setPendingDeleteItem] = useState<any>(null);
  const [formValues, setFormValues] = useState({
    benefit_section_title: '',
    benefit_section_description: '',
    benefits: [
      {
        title: '',
        description: ''
      }
    ]
  });

  useEffect(() => {
    setFormValues({
      benefit_section_title: companyData?.benefit_section_title || '',
      benefit_section_description:
        companyData?.benefit_section_description || '',
      benefits: benefitData?.benefits ||
        companyData?.benefits || [
          {
            title: '',
            description: ''
          }
        ]
    });
  }, [companyData]);

  const handleRemove = (benefit: any) => {
    setPendingDeleteItem(benefit);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (pendingDeleteItem) {
      const { page, id } = pendingDeleteItem;
      CMSService.removeBenefits(page, id);
    }
    setDeleteConfirmOpen(false);
    setPendingDeleteItem(null);
  };

  const cancelDelete = () => {
    setDeleteConfirmOpen(false);
    setPendingDeleteItem(null);
  };

  const handleSubmit = async (values: any, actions: FormikHelpers<any>) => {
    const { benefit_section_title, benefit_section_description, benefits } =
      values;
    if (setCompanyData) {
      setCompanyData((prev: any) => ({
        ...prev,
        benefit_section_title,
        benefit_section_description
      }));
      setBenFits((prev: any) => ({
        ...prev,
        benefits
      }));
      onSubmit();
    } else {
      const data: any = {};
      if (benefit_section_title !== companyData.benefit_section_title) {
        data.benefit_section_title = benefit_section_title;
      }
      if (
        benefit_section_description !== companyData.benefit_section_description
      ) {
        data.benefit_section_description = benefit_section_description;
      }
      const newBenefits: any[] = [];
      const changedBenefits: any[] = [];
      benefits.forEach((benefit: any, index: number) => {
        const originalBenefit = (companyData.benefits || [])[index] || {};
        if (benefit.isNew) {
          newBenefits.push(benefit);
        } else {
          const result = benefitHasChanged(benefit, originalBenefit);
          if (result) {
            changedBenefits.push(result);
          }
        }
      });
      data && setNewData(data);
      changedBenefits && setBenefitData(changedBenefits, false);
      newBenefits && setBenefitData(newBenefits, true);
    }
  };

  const modules = {
    toolbar: [
      [{ font: [] }],
      [{ size: ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ color: [] }, { background: [] }],
      ['clean']
    ]
  };

  return (
    <div
      className="who"
      onClick={isPopup ? (e) => e.stopPropagation() : () => {}}
    >
      {deleteConfirmOpen && (
        <Popup
          className="popUp"
          title={t('Remove Benefit')}
          onClose={cancelDelete}
        >
          <ConfirmPopup
            description={t('Are you sure you want to remove this benefit?')}
            onApprove={confirmDelete}
            onCancel={cancelDelete}
            approveTitle={t('Yes, Remove')}
            cancelTitle={t('Cancel')}
            t={t}
          />
        </Popup>
      )}
      <Activation
        showTopSide={showTopSide}
        title={t('Our benefits')}
        currentStep={7}
        className={
          isPopup ? '' : `activation-edu ${className ?? ''} ${className}`
        }
        headerIconDisable
        totalSteps={8}
        isCareerPage={true}
        handleModalClose={onClose}
        isPopup={true}
        showTitle={showTitle}
      >
        <Formik
          enableReinitialize
          initialValues={formValues}
          onSubmit={(values, actions) => handleSubmit(values, actions)}
          validationSchema={validationSchema(t)}
        >
          {({ isValid, values, touched, errors, setFieldValue }) => (
            <Form>
              <div className="organization scrollable">
                <div className="fields">
                  <span className="label">{t('Title')}</span>
                  <FormField
                    inputClassName="input"
                    fieldName="benefit_section_title"
                    placeholder="Text"
                    type={InputType.text}
                  />
                </div>
                <div className="fields">
                  <span className="label">{t('Description')}</span>
                  <ReactQuill
                    value={values.benefit_section_description}
                    onChange={(content) =>
                      setFieldValue('benefit_section_description', content)
                    }
                    modules={modules}
                    placeholder="Enter description"
                    className="quill-editor"
                  />
                  {touched.benefit_section_description &&
                  errors.benefit_section_description ? (
                    <div className="error">{t('Description is required')}</div>
                  ) : null}
                </div>
                <FieldArray name="benefits">
                  {({ push, remove }) => (
                    <>
                      {values?.benefits &&
                        values?.benefits?.map((benefit: any, index: number) => (
                          <div key={index}>
                            {isPopup && (
                              <div className="addJob">
                                <ActionButton
                                  iconName="remove.svg"
                                  onClick={() => handleRemove(benefit)}
                                />

                                <span>{t('Remove')}</span>
                              </div>
                            )}
                            <div className="field">
                              <span className="label">
                                {t('Name of benefit')}
                              </span>
                              <FormField
                                inputClassName={`input ${
                                  (errors as any)?.benefits?.[index]?.title &&
                                  (touched as any).benefits?.[index]?.title
                                    ? 'error'
                                    : ''
                                }`}
                                fieldName={`benefits.${index}.title`}
                                placeholder="Name"
                                type={InputType.text}
                              />

                              <ErrorMessage name={`benefits.${index}.title`}>
                                {(msg) => <div className="error-ms">{msg}</div>}
                              </ErrorMessage>
                            </div>
                            <div className="field">
                              <span className="label">{t('Description')}</span>
                              <FormField
                                inputClassName={`input ${
                                  (errors as any)?.benefits?.[index]
                                    ?.description &&
                                  (touched as any).benefits?.[index]
                                    ?.description
                                    ? 'error'
                                    : ''
                                }`}
                                fieldName={`benefits.${index}.description`}
                                placeholder="Description"
                                type={InputType.text}
                              />

                              <ErrorMessage
                                name={`benefits.${index}.description`}
                              >
                                {(msg) => <div className="error-ms">{msg}</div>}
                              </ErrorMessage>
                            </div>
                          </div>
                        ))}
                      <div className="addJob">
                        <ActionButton
                          iconName="plus.svg"
                          onClick={() => push({ isNew: true })}
                        />

                        <span>{t('Add Another Benefit')}</span>
                      </div>
                    </>
                  )}
                </FieldArray>
                {!isOneBtn ? (
                  <div className="button_container">
                    <Button
                      onClick={onBack}
                      t={t}
                      title="Back"
                      className="av__content__buttons-back"
                    />

                    <Button
                      className={`next ${!isValid ? 'invalid' : `${isValid}`}`}
                      type="submit"
                      title="Next"
                      t={t}
                    />
                  </div>
                ) : (
                  <div className="btn_container">
                    <Button
                      className="next button one-btn"
                      type="submit"
                      t={t}
                      title={'Save'}
                    />
                  </div>
                )}
              </div>
            </Form>
          )}
        </Formik>
      </Activation>
    </div>
  );
};

export default OurBenefits;
